import { API, getToken } from "./api";

const SESSION_KEY = "tc_session_id";
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

function sessionId() {
  let s = sessionStorage.getItem(SESSION_KEY);
  if (!s) {
    s = uuid();
    sessionStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

const describe = (el) => {
  const target = el.closest?.("[data-testid], button, a, [role=button], [role=menuitem], [role=tab], input, textarea, select, label") || el;
  const testid = target.getAttribute?.("data-testid") || el.getAttribute?.("data-testid");
  const text = (target.getAttribute?.("aria-label") || target.innerText || target.value || "").trim().replace(/\s+/g, " ").slice(0, 80);
  return {
    target: testid || target.tagName?.toLowerCase() || "unknown",
    label: text || testid || null,
    metadata: { tag: target.tagName?.toLowerCase(), testid: testid || null, href: target.getAttribute?.("href") || null, role: target.getAttribute?.("role") || null },
  };
};

class Analytics {
  constructor() {
    this.queue = [];
    this.timer = null;
    this.userId = null;
    this.context = { conversation_id: null };
    this.installed = false;
    this.tracking = { keystrokes: true, snapshots: true, heartbeats: true, reading: true };
    this.lastActivity = 0;
    this.interactions = 0;
  }

  setTracking(cfg) {
    if (cfg) this.tracking = { ...this.tracking, ...cfg };
  }

  on(flag) {
    return this.tracking[flag] !== false;
  }

  setUser(id) {
    this.userId = id;
    if (!id) this.queue = [];
  }

  setContext(ctx) {
    this.context = { ...this.context, ...ctx };
  }

  track(event_type, { category = "interaction", target = null, target_id = null, label = null, conversation_id, metadata = {} } = {}) {
    if (!getToken()) return;
    this.queue.push({
      event_type, category, target, target_id, label,
      path: window.location.pathname,
      conversation_id: conversation_id === undefined ? this.context.conversation_id : conversation_id,
      session_id: sessionId(),
      metadata: { ...metadata, viewport: `${window.innerWidth}x${window.innerHeight}` },
      client_ts: new Date().toISOString(),
    });
    if (this.queue.length >= 40) this.flush();
    else if (!this.timer) this.timer = setTimeout(() => this.flush(), 2500);
  }

  async flush(useBeacon = false) {
    clearTimeout(this.timer);
    this.timer = null;
    const token = getToken();
    if (!this.queue.length || !token) return;
    const events = this.queue.splice(0, 500);
    try {
      await fetch(`${API}/analytics/events`, {
        method: "POST", keepalive: true,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ events }),
      });
    } catch {
      if (!useBeacon) this.queue.unshift(...events.slice(0, 100));
    }
  }

  install() {
    if (this.installed) return;
    this.installed = true;
    document.addEventListener("click", (e) => {
      if (!(e.target instanceof Element)) return;
      const d = describe(e.target);
      this.track("click", { ...d, metadata: { ...d.metadata, x: e.clientX, y: e.clientY, button: e.button, modifiers: { shift: e.shiftKey, meta: e.metaKey || e.ctrlKey } } });
    }, true);
    document.addEventListener("contextmenu", (e) => {
      if (!(e.target instanceof Element)) return;
      this.track("context_menu", describe(e.target));
    }, true);
    document.addEventListener("focusin", (e) => {
      if (!(e.target instanceof Element)) return;
      if (!["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      this.track("field_focus", describe(e.target));
    }, true);
    document.addEventListener("copy", () => this.track("copy_text", { metadata: { length: (window.getSelection()?.toString() || "").length } }));
    document.addEventListener("keydown", (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.length === 1) this.track("keyboard_shortcut", { label: `${e.metaKey ? "cmd" : "ctrl"}+${e.key.toLowerCase()}`, metadata: { shift: e.shiftKey } });
    }, true);
    document.addEventListener("visibilitychange", () => {
      this.track("visibility_change", { category: "session", label: document.visibilityState });
      if (document.visibilityState === "hidden") this.flush(true);
    });
    window.addEventListener("focus", () => this.track("window_focus", { category: "session" }));
    window.addEventListener("blur", () => this.track("window_blur", { category: "session" }));
    window.addEventListener("pagehide", () => {
      this.track("session_end", { category: "session" });
      this.flush(true);
    });
    const bump = () => { this.lastActivity = Date.now(); this.interactions++; };
    ["mousedown", "keydown", "wheel", "touchstart", "scroll"].forEach((t) => document.addEventListener(t, bump, { capture: true, passive: true }));
    let lastMove = 0;
    document.addEventListener("mousemove", () => { const t = Date.now(); if (t - lastMove > 1000) { lastMove = t; bump(); } }, { passive: true });
    setInterval(() => {
      if (!this.on("heartbeats") || !this.userId || document.visibilityState !== "visible") { this.interactions = 0; return; }
      if (Date.now() - this.lastActivity < 60000) {
        this.track("heartbeat", { category: "session", metadata: { active_ms: 30000, interactions: this.interactions } });
      }
      this.interactions = 0;
    }, 30000);
    setInterval(() => this.flush(), 10000);
  }
}

export const analytics = new Analytics();
